// Einträge: your entries and the agents' time as a day or week calendar, or as a list with multi-select and bulk actions.
import { useMemo, useState, type MouseEvent } from 'react';
import type { Interval } from '../../../packages/core/src/index.ts';
import type { TimeEntry } from '../api/types.ts';
import { addDays, calendarExtent, dayRange, HOUR, weekRange } from '../lib/calendar.ts';
import { navigate } from '../lib/router.ts';
import { activeProjects, displayTitle, project as findProject, providerInfo } from '../model/data.ts';
import * as m from '../model/mutations.ts';
import { buildSnapshot, entryDays, type AgentBlock, type EntryDay } from '../model/snapshot.ts';
import { Banner, Confirm, EmptyState, Figure, Loading, Menu, MenuDivider, MenuItem, ScreenHeader } from '../components/common.tsx';
import { useFmt, useMedia, useNow, useRange, useStore } from '../store/hooks.tsx';
import type { EntriesMode } from '../store/store.ts';
import { Button, cx, EntryRow, GlassPanel, SegmentedControl } from '../ui/ds.tsx';
import { Icon } from '../ui/Icon.tsx';

export function entriesRange(mode: EntriesMode, anchor: number, timeZone: string): Interval {
  return mode === 'day' ? dayRange(anchor, timeZone) : weekRange(anchor, timeZone);
}

export function Entries() {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const now = useNow(1000);
  const phone = useMedia('(max-width: 639.98px)');
  const mode: EntriesMode = phone && store.ui.entriesMode === 'week' ? 'day' : store.ui.entriesMode;
  const anchor = store.ui.entriesAnchor ?? now;
  const range = useMemo(() => entriesRange(mode, anchor, store.timeZone), [mode, Math.floor(anchor / 60_000), store.timeZone]);
  const loaded = useRange(range);
  const data = loaded.data;
  const days = data ? entryDays(store.ws, data, range, now, store.timeZone) : undefined;
  const snap = data ? buildSnapshot(store.ws, data, range, now) : undefined;
  const isCurrent = range.start <= now && now < range.end;
  const step = (direction: number) => store.setUi({
    entriesAnchor: mode === 'day' ? addDays(anchor, direction, store.timeZone) : addDays(anchor, direction * 7, store.timeZone), entrySelection: [],
  });
  const title = mode === 'day' ? fmt.fullDate(range.start)
    : `${t('Woche %lld', fmt.week(range.start))} · ${fmt.dayMonthShort(range.start)} – ${fmt.dayMonthYear(range.end - 1)}`;
  const modes: { value: EntriesMode; label: string }[] = phone
    ? [{ value: 'day', label: t('Tag') }, { value: 'list', label: t('Liste') }]
    : [{ value: 'day', label: t('Tag') }, { value: 'week', label: t('Woche') }, { value: 'list', label: t('Liste') }];
  return (
    <div className="screen">
      <ScreenHeader title={t('Einträge')} subtitle={title}>
        <span className="stepper">
          <Button variant="plain" size="sm" iconOnly icon="chevronLeft" aria-label={mode === 'day' ? t('Vorheriger Tag') : t('Vorherige Woche')} onClick={() => step(-1)} />
          <Button size="sm" disabled={isCurrent} onClick={() => store.setUi({ entriesAnchor: null, entrySelection: [] })}>{t('Heute')}</Button>
          <Button variant="plain" size="sm" iconOnly icon="chevron" aria-label={mode === 'day' ? t('Nächster Tag') : t('Nächste Woche')} onClick={() => step(1)} />
        </span>
        <SegmentedControl<EntriesMode> label={t('Ansicht')} value={mode} options={modes} onChange={(v) => store.setUi({ entriesMode: v, entrySelection: [] })} />
        <Button icon="plus" onClick={() => store.openSheet({ kind: 'entry', entryId: null, day: null })}>{t('Eintrag')}</Button>
      </ScreenHeader>
      {snap ? (
        <div className="figures">
          <Figure label={t('Abgerechnet')} value={fmt.duration(snap.mergedMs)} strong />
          <Figure label={t('Eigene Zeit')} value={fmt.duration(snap.humanMs)} dot="human" />
          <Figure label={t('Agent-Zeit')} value={fmt.duration(snap.agentMs)} dot="agent" />
          <Figure label={t('Überschneidung 1× gezählt')} value={fmt.duration(snap.overlapMs)} />
          <Figure label={t('Abrechenbar')} value={fmt.money(snap.amountCents)} />
        </div>
      ) : null}
      {!days ? (loaded.error ? <Banner tone="danger">{loaded.error}</Banner> : <Loading label={t('Lädt …')} />) : days.every((d) => d.isEmpty) ? (
        <EmptyState icon="calendar" title={mode === 'day' ? t('Keine Einträge an diesem Tag') : t('Keine Einträge in dieser Woche')}
          message={t('Trag Zeit nach oder starte den Timer. Agent-Zeit erscheint hier von selbst.')}>
          <Button icon="plus" onClick={() => store.openSheet({ kind: 'entry', entryId: null, day: range.start + 9 * HOUR })}>{t('Eintrag nachtragen')}</Button>
        </EmptyState>
      ) : mode === 'list' ? (
        <EntriesList days={[...days].reverse().filter((d) => !d.isEmpty)} now={now} entries={data?.entries ?? []} />
      ) : (
        <Calendar days={days} now={now} compact={days.length > 1} />
      )}
    </div>
  );
}

// MARK: List

function EntriesList({ days, now, entries }: { days: EntryDay[]; now: number; entries: TimeEntry[] }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const selection = new Set(store.ui.entrySelection);
  const toggle = (id: string) => {
    const next = new Set(selection);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    store.setUi({ entrySelection: [...next] });
  };
  return (
    <>
      <div className="tl-card entries-list">
        {days.map((day) => {
          const rows = [
            ...day.entries.map((e) => ({ id: e.id, start: e.startAt, human: e as TimeEntry | null, agent: null as AgentBlock | null })),
            ...day.agents.map((b) => ({ id: `agent:${b.id}`, start: b.start, human: null, agent: b })),
          ].sort((a, b) => b.start - a.start);
          return (
            <section key={day.day.start} className="entries-day">
              <div className="entries-day__head">
                <h3>{fmt.weekdayDayMonth(day.day.start)}</h3>
                <small>{t('%@ abgerechnet', fmt.duration(day.billedMs))}</small>
              </div>
              {rows.map((row) => {
                if (row.human) {
                  const e = row.human;
                  const end = e.endAt ?? now;
                  const p = findProject(store.ws, e.projectId);
                  const from = Math.max(e.startAt, day.day.start);
                  const to = Math.min(end, day.day.end);
                  return (
                    <div key={row.id} className={cx('entry-line', selection.has(e.id) && 'is-selected')}>
                      <input type="checkbox" className="entry-line__check" checked={selection.has(e.id)} onChange={() => toggle(e.id)}
                        aria-label={t('Auswählen: %@', e.description || t('Ohne Beschreibung'))} />
                      <button type="button" className="row-button entry-line__row" onClick={() => store.openSheet({ kind: 'entry', entryId: e.id, day: null })}>
                        <EntryRow from={fmt.time(from)} to={fmt.time(to)} description={e.description} source="human" ms={to - from} running={e.endAt === null}
                          project={p ? { name: p.name, color: p.colorSlot } : undefined} billable={e.billable && (p?.billable ?? true)} tags={e.tags} />
                      </button>
                    </div>
                  );
                }
                const b = row.agent as AgentBlock;
                return (
                  <div key={row.id} className="entry-line">
                    <span className="entry-line__check" aria-hidden="true" />
                    <button type="button" className="row-button entry-line__row" onClick={() => navigate({ name: 'session', id: b.session.id })}>
                      <EntryRow from={fmt.time(b.start)} to={fmt.time(b.end)} description={displayTitle(b.session)} source="agent"
                        sourceLabel={providerInfo(b.session.provider).name} ms={b.countedMs} billable={b.project?.billable ?? false}
                        project={b.project ? { name: b.project.name, color: b.project.colorSlot } : undefined}
                        running={b.session.state === 'working' && b.end >= now - 1000} />
                    </button>
                  </div>
                );
              })}
            </section>
          );
        })}
      </div>
      {selection.size > 0 ? <BulkBar ids={[...selection]} entries={entries} /> : null}
    </>
  );
}

function BulkBar({ ids, entries }: { ids: string[]; entries: TimeEntry[] }) {
  const store = useStore();
  const t = store.t;
  const [confirm, setConfirm] = useState(false);
  const selected = entries.filter((e) => ids.includes(e.id));
  const done = () => store.setUi({ entrySelection: [] });
  const apply = (change: (e: TimeEntry) => TimeEntry) => void store.apply(m.bulkUpdate(store.actor(), selected, change, store.clock()));
  return (
    <GlassPanel shape="capsule" className="bulkbar" role="toolbar" aria-label={t('Auswahl')}>
      <span className="bulkbar__count">{t('%lld ausgewählt', ids.length)}</span>
      <span className="spacer" />
      <Menu label={t('Projekt zuordnen')} trigger={(props) => <Button size="sm" icon="folder" {...props}>{t('Projekt zuordnen')}</Button>}>
        {(menu) => (
          <>
            {activeProjects(store.ws).map((p) => (
              <MenuItem key={p.id} onSelect={() => { menu.close(); apply((e) => ({ ...e, projectId: p.id, billable: p.billable })); }}>
                <span className="menu__proj"><span className="tl-dot" style={{ background: `var(--project-${p.colorSlot})` }} />{p.name}</span>
              </MenuItem>
            ))}
            <MenuDivider />
            <MenuItem onSelect={() => { menu.close(); apply((e) => ({ ...e, projectId: null, billable: true })); }}>{t('Ohne Projekt')}</MenuItem>
          </>
        )}
      </Menu>
      <Menu label={t('Abrechnung')} trigger={(props) => <Button size="sm" icon="euro" {...props}>{t('Abrechnung')}</Button>}>
        {(menu) => (
          <>
            <MenuItem onSelect={() => { menu.close(); apply((e) => ({ ...e, billable: true })); }}>{t('Abrechenbar')}</MenuItem>
            <MenuItem onSelect={() => { menu.close(); apply((e) => ({ ...e, billable: false })); }}>{t('Nicht abrechenbar')}</MenuItem>
          </>
        )}
      </Menu>
      <Button variant="plain" size="sm" onClick={() => setConfirm(true)}>{t('Löschen …')}</Button>
      <Button variant="plain" size="sm" onClick={done}>{t('Auswahl aufheben')}</Button>
      {confirm ? (
        <Confirm title={t('%lld Einträge löschen?', ids.length)} danger confirm={t('Löschen')} cancel={t('Abbrechen')} onClose={() => setConfirm(false)}
          message={t('Gelöschte Einträge bleiben im Verlauf und lassen sich wiederherstellen.')}
          onConfirm={() => { void store.apply(m.deleteEntries(store.actor(), selected, store.clock())); done(); }} />
      ) : null}
    </GlassPanel>
  );
}

// MARK: Calendar

interface Slot {
  id: string;
  start: number;
  end: number;
  column: number;
  columns: number;
}

/** Overlapping blocks share the width: greedy columns per cluster of overlaps. */
function layout(slots: Omit<Slot, 'column' | 'columns'>[], top: number): Slot[] {
  const sorted = slots.filter((s) => s.end > s.start && s.end > top).sort((a, b) => a.start - b.start || a.end - b.end);
  const result: Slot[] = [];
  let cluster: Slot[] = [];
  let clusterEnd = 0;
  const flush = () => {
    const count = Math.max(0, ...cluster.map((s) => s.column)) + 1;
    result.push(...cluster.map((s) => ({ ...s, columns: count })));
    cluster = [];
  };
  for (const s of sorted) {
    if (cluster.length > 0 && s.start >= clusterEnd) flush();
    const used = new Set(cluster.filter((o) => o.end > s.start).map((o) => o.column));
    let column = 0;
    while (used.has(column)) column += 1;
    cluster.push({ ...s, column, columns: 1 });
    clusterEnd = Math.max(clusterEnd, s.end);
  }
  flush();
  return result;
}

function Calendar({ days, now, compact }: { days: EntryDay[]; now: number; compact: boolean }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const hourHeight = compact ? 44 : 56;
  const { first, last } = calendarExtent(days.map((day) => ({ day: day.day,
    times: [...day.entries.flatMap((e) => [e.startAt, e.endAt ?? now]), ...day.agents.flatMap((a) => [a.start, a.end])],
  })), store.timeZone);
  const hours = Array.from({ length: last - first }, (_, i) => first + i);
  return (
    <div className="tl-card cal">
      <div className={cx('cal__scroll', compact && 'cal__scroll--week')}>
        <div className="cal__grid" style={{ gridTemplateColumns: `52px repeat(${days.length}, minmax(0, 1fr))` }}>
          <div />
          {days.map((day) => {
            const today = day.day.start <= now && now < day.day.end;
            return (
              <div key={day.day.start} className="cal__dayhead">
                <div className="cal__daytitle">
                  <span className={today ? 'is-today' : undefined}>{compact ? fmt.weekdayShortDay(day.day.start) : fmt.weekdayDayMonth(day.day.start)}</span>
                  {today ? <span className="cal__today">{t('Heute')}</span> : null}
                </div>
                <div className="cal__daysub">{day.billedMs > 0 ? t('%@ abgerechnet', fmt.duration(day.billedMs)) : t('nichts erfasst')}</div>
                {!compact ? (
                  <div className="cal__coltitles">
                    <span className="cal__coltitle cal__coltitle--human"><Icon name="user" size={13} strokeWidth={2} />{t('Eigene Zeit')}</span>
                    <span className="cal__coltitle cal__coltitle--agent"><Icon name="terminal" size={13} strokeWidth={2} />{t('Agents')}</span>
                  </div>
                ) : null}
              </div>
            );
          })}
          <div className="cal__axis" aria-hidden="true">
            {hours.map((h) => <span key={h} style={{ height: hourHeight }}>{fmt.time(days[0]!.day.start + h * HOUR)}</span>)}
          </div>
          {days.map((day) => <DayColumn key={day.day.start} day={day} first={first} hours={hours.length} hourHeight={hourHeight} now={now} compact={compact} reference={days[0]!.day.start} />)}
        </div>
      </div>
    </div>
  );
}

function DayColumn({ day, first, hours, hourHeight, now, compact, reference }: { day: EntryDay; first: number; hours: number; hourHeight: number; now: number; compact: boolean; reference: number }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const top = day.day.start + first * HOUR;
  const y = (x: number) => ((x - top) / HOUR) * hourHeight;
  const height = hours * hourHeight;
  const human = layout(day.entries.map((e) => ({ id: e.id, start: Math.max(e.startAt, day.day.start), end: Math.min(e.endAt ?? now, day.day.end) })), top);
  const agents = layout(day.agents.flatMap((b) => b.intervals.map((i, n) => ({ id: `${b.id}#${n}`, start: i.start, end: i.end }))), top);
  const humanWidth = compact ? 64 : 49;
  const agentLeft = compact ? 66 : 51;
  const newAt = (e: MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const quarter = Math.max(0, Math.min(Math.ceil((day.day.end - top) / 900_000) - 1, Math.floor(((e.clientY - r.top) / hourHeight) * 4)));
    store.openSheet({ kind: 'entry', entryId: null, day: top + quarter * 900_000 });
  };
  return (
    <div className="cal__col" style={{ height }}>
      {Array.from({ length: hours }, (_, i) => <div key={i} className="cal__line" style={{ top: i * hourHeight }}>
        {compact && fmt.time(top + i * HOUR) !== fmt.time(reference + (first + i) * HOUR) && top + i * HOUR < day.day.end
          ? <span className="cal__tick" aria-hidden="true">{fmt.time(top + i * HOUR)}</span> : null}
      </div>)}
      <div className="cal__free" style={{ width: `${humanWidth}%`, height }} onDoubleClick={newAt} title={t('Doppelklick: Eintrag nachtragen')} />
      {human.map((slot) => {
        const entry = day.entries.find((e) => e.id === slot.id);
        if (!entry) return null;
        const p = findProject(store.ws, entry.projectId);
        const w = humanWidth / slot.columns;
        const end = entry.endAt ?? now;
        return (
          <button key={slot.id} type="button" className={cx('cal-block', 'cal-block--human', y(slot.end) - y(slot.start) < 36 && 'cal-block--short', entry.endAt === null && 'is-running')}
            style={{ top: y(slot.start) + 1, height: Math.max(18, y(slot.end) - y(slot.start) - 2), left: `calc(${w * slot.column}% + 4px)`, width: `calc(${w}% - 3px)` }}
            title={`${entry.description} · ${fmt.time(entry.startAt)}–${fmt.time(end)} · ${fmt.duration(end - entry.startAt)}`}
            aria-label={t('Eigene Zeit: %@, %@ bis %@, %@', entry.description || t('Ohne Beschreibung'), fmt.time(entry.startAt), fmt.time(end), fmt.spoken(end - entry.startAt))}
            onClick={() => store.openSheet({ kind: 'entry', entryId: entry.id, day: null })}>
            <span className="cal-block__title">{entry.description || t('Ohne Beschreibung')}</span>
            <span className="cal-block__meta">
              {p ? <span className="tl-dot" style={{ background: `var(--project-${p.colorSlot})`, width: 6, height: 6 }} /> : null}
              {compact ? fmt.time(entry.startAt) : `${fmt.time(entry.startAt)}–${entry.endAt === null ? t('jetzt') : fmt.time(end)} · ${p?.name ?? t('ohne Projekt')}`}
            </span>
          </button>
        );
      })}
      {agents.map((slot) => {
        const block = day.agents.find((b) => slot.id.startsWith(`${b.id}#`));
        if (!block) return null;
        const w = (100 - agentLeft) / slot.columns;
        const h = y(slot.end) - y(slot.start);
        const provider = providerInfo(block.session.provider).name;
        return (
          <button key={slot.id} type="button" className="cal-block cal-block--agent"
            style={{ top: y(slot.start) + 0.5, height: Math.max(6, h - 1), left: `calc(${agentLeft + w * slot.column}%)`, width: `calc(${w}% - 4px)` }}
            title={`${provider} · ${displayTitle(block.session)}\n${fmt.time(slot.start)}–${fmt.time(slot.end)} · ${fmt.duration(slot.end - slot.start)} ${t('gezählt')}`}
            aria-label={t('%@: %@, %@ bis %@, %@ gezählt', provider, displayTitle(block.session), fmt.time(slot.start), fmt.time(slot.end), fmt.spoken(slot.end - slot.start))}
            onClick={() => navigate({ name: 'session', id: block.session.id })}>
            {h >= 30 ? <><span className="cal-block__title cal-block__title--agent">{provider}</span><span className="cal-block__meta">{displayTitle(block.session)}</span></> : null}
          </button>
        );
      })}
      {day.day.start <= now && now < day.day.end && now > top ? <div className="cal__now" style={{ top: y(now) - 1 }} /> : null}
    </div>
  );
}
